import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, Globe, MessageCircle, Instagram, Facebook, Plus, Trash2, Mail, HelpCircle, Users, Share2, Linkedin, Twitter, Youtube } from 'lucide-react';
import TikTokIcon from '@/components/icons/TikTokIcon';
import { useOrgaConfigurationContext } from '@/app/providers';
import { OrgaConfigurationRepository } from '@/services/firebase/orga-configuration-repository';
import { Card, CardBody, Button } from '@/components/ui';
import PhoneInput from '@/features/order-journey/components/PhoneInput';
import { useOrderPersonalization } from '@/shared/hooks/useOrderPersonalization';

const inputClasses = "w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all duration-200 bg-background-tertiary hover:bg-surface placeholder:text-foreground-secondary text-sm text-foreground";
const labelClasses = "block text-sm font-semibold text-foreground mb-2";

// Constants definitions moved inside component for translation

interface InternalContact {
  name: string;
  email?: string;
  phone?: string;
  position?: string;
}

interface ContactInformationFormData {
  contacts: InternalContact[];
  afterSalesService: {
    isSavChatEnabled: boolean;
    contacts: { type: 'email' | 'phone' | 'whatsapp'; value: string; label?: string }[];
    descriptionFr?: string;
    descriptionEn?: string;
    descriptionEs?: string;
  };
  socials: { key: string; value: string }[];
}

const ContactInformationConfig: React.FC = () => {
  const { t } = useTranslation();

  const SAV_LABELS = [
    { value: 'support_technique', label: t('admin.configuration.contact.sav.labels.support_technique') },
    { value: 'service_client', label: t('admin.configuration.contact.sav.labels.service_client') },
    { value: 'service_apres_vente', label: t('admin.configuration.contact.sav.labels.service_apres_vente') },
    { value: 'facturation', label: t('admin.configuration.contact.sav.labels.facturation') },
    { value: 'retours', label: t('admin.configuration.contact.sav.labels.retours') },
    { value: 'commercial', label: t('admin.configuration.contact.sav.labels.commercial') },
    { value: 'partenariat', label: t('admin.configuration.contact.sav.labels.partenariat') },
    { value: 'presse', label: t('admin.configuration.contact.sav.labels.presse') },
    { value: 'autre', label: t('admin.configuration.contact.sav.labels.autre') }
  ];

  const SOCIAL_NETWORKS = [
    { value: 'instagram', label: 'Instagram', icon: Instagram },
    { value: 'facebook', label: 'Facebook', icon: Facebook },
    { value: 'tiktok', label: 'TikTok', icon: TikTokIcon },
    { value: 'linkedin', label: 'LinkedIn', icon: Linkedin },
    { value: 'twitter', label: 'X (Twitter)', icon: Twitter },
    { value: 'youtube', label: 'YouTube', icon: Youtube },
    { value: 'website', label: t('admin.configuration.contact.website'), icon: Globe },
    { value: 'pinterest', label: 'Pinterest', icon: Globe },
    { value: 'snapchat', label: 'Snapchat', icon: MessageCircle },
  ];

  const INTERNAL_ROLES = [
    { value: 'commercial', label: t('admin.configuration.contact.internal.roles.commercial') },
    { value: 'sav', label: t('admin.configuration.contact.internal.roles.sav') },
    { value: 'dirigeant', label: t('admin.configuration.contact.internal.roles.dirigeant') },
    { value: 'responsable', label: t('admin.configuration.contact.internal.roles.responsable') },
    { value: 'autre', label: t('admin.configuration.contact.internal.roles.autre') },
  ];

  const CONTACT_TYPES = [
    { value: 'email', label: t('admin.configuration.general.email'), icon: Mail },
    { value: 'phone', label: t('admin.configuration.contact.phone'), icon: Phone },
    { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  ];

  const { idorga } = useParams<{ idorga: string }>();
  const { configuration, refreshConfiguration } = useOrgaConfigurationContext();
  const personalization = useOrderPersonalization();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'internal' | 'support' | 'socials'>('support');
  const [savLang, setSavLang] = useState<'fr' | 'en' | 'es'>('fr');

  const form = useForm<ContactInformationFormData>({
    mode: 'onChange',
    defaultValues: {
      contacts: [],
      afterSalesService: {
        isSavChatEnabled: false,
        contacts: [],
        descriptionFr: '',
        descriptionEn: '',
        descriptionEs: '',
      },
      socials: [],
    }
  });

  const { fields: contactFields, append: appendContact, remove: removeContact } = useFieldArray({
    control: form.control,
    name: "contacts"
  });

  const { fields: savContactFields, append: appendSavContact, remove: removeSavContact } = useFieldArray({
    control: form.control,
    name: "afterSalesService.contacts"
  });

  const { fields: socialFields, append: appendSocial, remove: removeSocial } = useFieldArray({
    control: form.control,
    name: "socials"
  });

  // Check if sections have content
  const hasSavContacts = savContactFields.length > 0 && savContactFields.some(c => c.value);
  const hasInternalContacts = contactFields.length > 0 && contactFields.some(c => c.name);
  const hasSocials = socialFields.length > 0 && socialFields.some(c => c.value);

  // Update form when configuration loads
  useEffect(() => {
    if (configuration) {
      // Helper to convert socials Record<string,string> to array
      const recordToArray = (record?: Record<string, string>) => {
        if (!record) return [];
        return Object.entries(record).map(([key, value]) => ({ key, value }));
      };

      // Helper to prepare SAV contacts (migration or new structure)
      const prepareSavContacts = () => {
        if (configuration.afterSalesService?.contacts && Array.isArray(configuration.afterSalesService.contacts)) {
          return configuration.afterSalesService.contacts;
        }

        // Fallback / Migration logic for old fields
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const legacySav = configuration.afterSalesService as any;
        const contacts = [];
        if (legacySav?.email) {
          contacts.push({ type: 'email' as const, value: legacySav.email, label: 'Email Support' });
        }
        if (legacySav?.phone) {
          contacts.push({ type: 'phone' as const, value: legacySav.phone, label: 'Téléphone Support' });
        }
        if (legacySav?.whatsapp) {
          contacts.push({ type: 'whatsapp' as const, value: legacySav.whatsapp, label: 'WhatsApp Support' });
        }
        return contacts;
      };

      form.reset({
        contacts: Array.isArray(configuration.contacts) && configuration.contacts.length > 0
          ? (configuration.contacts as InternalContact[])
          : [{ name: '', position: '', email: '', phone: '' }],
        afterSalesService: {
          isSavChatEnabled: configuration.afterSalesService?.isSavChatEnabled ?? false,
          contacts: prepareSavContacts().length > 0 ? prepareSavContacts() : [{ type: 'email', value: '', label: '' }],
          descriptionFr: configuration.afterSalesService?.description?.fr || '',
          descriptionEn: configuration.afterSalesService?.description?.en || '',
          descriptionEs: configuration.afterSalesService?.description?.es || '',
        },
        socials: recordToArray(configuration.socials).length > 0 ? recordToArray(configuration.socials) : [{ key: '', value: '' }],
      });
    }
  }, [configuration, form]);

  const handleSave = async (data: ContactInformationFormData) => {
    if (!idorga) return;

    // Validation: Require at least one SAV contact
    if (!data.afterSalesService.contacts || data.afterSalesService.contacts.length === 0) {
      toast.error(t('admin.configuration.contact.sav.required')); // Ensure this key exists or use a generic error
      // Or hardcode for now if translation key might be missing
      // toast.error("Au moins un contact SAV est requis");
      return;
    }

    setIsSaving(true);
    try {
      await OrgaConfigurationRepository.update(idorga, {
        contacts: data.contacts,
        afterSalesService: {
          isSavChatEnabled: data.afterSalesService.isSavChatEnabled,
          contacts: data.afterSalesService.contacts,
          description: {
            fr: data.afterSalesService.descriptionFr || '',
            en: data.afterSalesService.descriptionEn || '',
            es: data.afterSalesService.descriptionEs || '',
          }
        },
        socials: data.socials.reduce((acc, curr) => {
          if (curr.key && curr.value) acc[curr.key] = curr.value;
          return acc;
        }, {} as Record<string, string>),
      });

      await refreshConfiguration();
      setIsEditing(false);
      toast.success(t('admin.configuration.general.success'));
    } catch (error) {
      console.error('Error updating contact info:', error);
      toast.error(t('admin.configuration.general.error'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!configuration) return null;

  return (
    <div className="space-y-6 min-h-[600px]">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-foreground leading-tight">{t('admin.configuration.contact.title')}</h2>
          <p className="text-xs sm:text-sm text-foreground-secondary mt-0.5 sm:mt-1">{t('admin.configuration.contact.description')}</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          {!isEditing ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="w-full sm:w-auto"
            >
              {t('admin.configuration.general.edit')}
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsEditing(false);
                  form.reset();
                }}
                disabled={isSaving}
                className="flex-1 sm:flex-none sm:w-auto"
              >
                {t('admin.configuration.general.cancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={form.handleSubmit(handleSave)}
                disabled={isSaving || !form.formState.isValid}
                isLoading={isSaving}
                className="flex-1 sm:flex-none sm:w-auto"
              >
                {t('admin.configuration.general.save')}
              </Button>
            </>
          )}
        </div>
      </div>

      <Card variant="default">
        <CardBody className="p-6">

          {/* Tabs */}
          <div className="flex border-b border-border mb-6">
            <button
              className={`px-4 py-2 font-medium text-sm focus:outline-none transition-colors ${activeTab === 'support'
                ? 'text-primary border-b-2 border-primary'
                : 'text-foreground-secondary hover:text-foreground'
                }`}
              onClick={() => setActiveTab('support')}
            >
              <div className="flex items-center gap-2">
                <HelpCircle size={16} />
                {t('admin.configuration.contact.sav.title')}
                {!hasSavContacts && (
                  <span className="w-2 h-2 rounded-full bg-warning" title="Configuration requise" />
                )}
              </div>
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm focus:outline-none transition-colors ${activeTab === 'internal'
                ? 'text-primary border-b-2 border-primary'
                : 'text-foreground-secondary hover:text-foreground'
                }`}
              onClick={() => setActiveTab('internal')}
            >
              <div className="flex items-center gap-2">
                <Users size={16} />
                {t('admin.configuration.contact.internal.title')}
                {!hasInternalContacts && (
                  <span className="w-2 h-2 rounded-full bg-warning" title="Configuration recommandée" />
                )}
              </div>
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm focus:outline-none transition-colors ${activeTab === 'socials'
                ? 'text-primary border-b-2 border-primary'
                : 'text-foreground-secondary hover:text-foreground'
                }`}
              onClick={() => setActiveTab('socials')}
            >
              <div className="flex items-center gap-2">
                <Share2 size={16} />
                {t('admin.configuration.contact.socials.title')}
                {!hasSocials && (
                  <span className="w-2 h-2 rounded-full bg-warning" title="Configuration recommandée" />
                )}
              </div>
            </button>
          </div>

          <form onSubmit={form.handleSubmit(handleSave)} className="space-y-6">

            {/* TAB: SAV (Customer Support) */}
            {activeTab === 'support' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-surface rounded-xl border border-border mb-6">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Activer le Chat SAV</h3>
                    <p className="text-xs text-foreground-secondary mt-1">
                      Permet aux clients de vous contacter directement via une messagerie instantanée
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={!isEditing}
                    onClick={() => {
                      if (isEditing) {
                        const current = form.getValues('afterSalesService.isSavChatEnabled');
                        form.setValue('afterSalesService.isSavChatEnabled', !current, { shouldDirty: true });
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''
                      } ${form.watch('afterSalesService.isSavChatEnabled') ? 'bg-primary' : 'bg-gray-200'
                      }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.watch('afterSalesService.isSavChatEnabled') ? 'translate-x-6' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </div>

                <p className="text-sm text-foreground-secondary italic">
                  {t('admin.configuration.contact.sav.description')}
                </p>

                <div className="space-y-4">
                  {savContactFields.map((field, index) => {
                    const selectedType = form.watch(`afterSalesService.contacts.${index}.type`);
                    const typeInfo = CONTACT_TYPES.find(t => t.value === selectedType) || CONTACT_TYPES[0];
                    const Icon = typeInfo.icon;

                    return (
                      <div key={field.id} className="relative group bg-surface rounded-xl border border-border p-4 hover:shadow-sm transition-all">
                        {isEditing && (
                          <button
                            type="button"
                            onClick={() => removeSavContact(index)}
                            className="absolute top-2 right-2 p-2 text-error hover:bg-error-10 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-start">
                          <div className="relative sm:col-span-12 md:col-span-3">
                            <label className={labelClasses}>{t('admin.configuration.contact.sav.type')} *</label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none h-full">
                                <Icon className="h-4 w-4 text-foreground-secondary" />
                              </div>
                              <select
                                {...form.register(`afterSalesService.contacts.${index}.type` as const, { required: isEditing })}
                                disabled={!isEditing}
                                className={`${inputClasses} pl-10 appearance-none bg-surface`}
                              >
                                {CONTACT_TYPES.map((type) => (
                                  <option key={type.value} value={type.value}>
                                    {type.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="sm:col-span-12 md:col-span-5">
                            <label className={labelClasses}>{t('admin.configuration.contact.sav.value')} *</label>
                            <input
                              {...form.register(`afterSalesService.contacts.${index}.value` as const, {
                                required: isEditing,
                                pattern: selectedType === 'email' ? {
                                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                  message: "Format d'email invalide"
                                } : undefined
                              })}
                              disabled={!isEditing}
                              placeholder={selectedType === 'email' ? 'exemple@email.com' : '+33 6...'}
                              className={`${inputClasses} ${form.formState.errors.afterSalesService?.contacts?.[index]?.value ? 'border-error' : ''}`}
                            />
                            {form.formState.errors.afterSalesService?.contacts?.[index]?.value && (
                              <p className="mt-1 text-xs text-error">
                                {form.formState.errors.afterSalesService.contacts[index]?.value?.message || "Ce champ est requis"}
                              </p>
                            )}
                          </div>
                          <div className="sm:col-span-12 md:col-span-4">
                            <label className={labelClasses}>{t('admin.configuration.contact.sav.label')} *</label>
                            <div className="relative">
                              <select
                                {...form.register(`afterSalesService.contacts.${index}.label` as const, { required: isEditing })}
                                disabled={!isEditing}
                                className={`${inputClasses} appearance-none bg-surface`}
                              >
                                <option value="">Sélectionner...</option>
                                {SAV_LABELS.map((label) => (
                                  <option key={label.value} value={label.value}>
                                    {label.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => { if (!isEditing) setIsEditing(true); appendSavContact({ type: 'email', value: '', label: '' }); }}
                      className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-dark px-3 py-2 rounded-lg hover:bg-primary-10 transition-colors"
                    >
                      <Plus size={16} />
                      {t('admin.configuration.contact.sav.add')}
                    </button>
                  )}
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-medium text-foreground">{t('admin.configuration.contact.sav.descLabel')}</h3>
                    {/* Language Selector */}
                    <div className="flex space-x-2">
                      {(['fr', 'en', 'es'] as const).map((lang) => (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => setSavLang(lang)}
                          className={`px-2 py-1 text-xs font-medium rounded transition-colors ${savLang === lang
                            ? 'bg-primary-10 text-primary-dark border border-primary-20'
                            : 'bg-surface text-foreground-secondary border border-border hover:bg-background-tertiary'
                            }`}
                        >
                          {lang.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="relative">
                    <textarea
                      {...form.register(
                        savLang === 'fr'
                          ? 'afterSalesService.descriptionFr'
                          : savLang === 'en'
                            ? 'afterSalesService.descriptionEn'
                            : 'afterSalesService.descriptionEs'
                      )}
                      disabled={!isEditing}
                      rows={3}
                      className={inputClasses}
                      placeholder={t('admin.configuration.contact.sav.descPlaceholder', { lang: savLang.toUpperCase() })}
                    />
                    <p className="mt-2 text-xs text-foreground-secondary italic">
                      {t('admin.configuration.contact.sav.descHelper')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* TAB: Contacts (Internal) */}
            {activeTab === 'internal' && (
              <div className="space-y-4">
                <p className="text-sm text-foreground-secondary italic">
                  {t('admin.configuration.contact.internal.description')}
                </p>

                {contactFields.map((field, index) => (
                  <div key={field.id} className="relative group bg-surface rounded-xl border border-border p-4 hover:shadow-sm transition-all">
                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => removeContact(index)}
                        className="absolute top-2 right-2 p-2 text-error hover:bg-error-10 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className={labelClasses}>Nom complet *</label>
                          <input
                            {...form.register(`contacts.${index}.name` as const, { required: isEditing })}
                            disabled={!isEditing}
                            placeholder="Jean Dupont"
                            className={inputClasses}
                          />
                        </div>
                        <div>
                          <label className={labelClasses}>Poste *</label>
                          <select
                            {...form.register(`contacts.${index}.position` as const, { required: isEditing })}
                            disabled={!isEditing}
                            className={inputClasses}
                          >
                            <option value="">Sélectionner un poste...</option>
                            {INTERNAL_ROLES.map((role) => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                          {form.formState.errors.contacts?.[index]?.position && (
                            <p className="mt-1 text-xs text-error">
                              {t('admin.validation.required')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className={labelClasses}>{t('admin.configuration.general.email')} *</label>
                          <input
                            type="email"
                            {...form.register(`contacts.${index}.email` as const, {
                              required: isEditing,
                              pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: "Format d'email invalide"
                              }
                            })}
                            disabled={!isEditing}
                            placeholder="contact@example.com"
                            className={`${inputClasses} ${form.formState.errors.contacts?.[index]?.email ? 'border-error' : ''}`}
                          />
                          {form.formState.errors.contacts?.[index]?.email && (
                            <p className="mt-1 text-xs text-error">
                              {form.formState.errors.contacts[index]?.email?.message || "Ce champ est requis"}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className={labelClasses}>{t('admin.configuration.contact.phone')} *</label>
                          <PhoneInput
                            value={form.watch(`contacts.${index}.phone`) || ''}
                            onChange={(value) => form.setValue(`contacts.${index}.phone`, value)}
                            disabled={!isEditing}
                            placeholder="+33 1 23 45 67 89"
                            personalization={{
                              input: {
                                background: personalization.input.background,
                                border: personalization.input.border,
                                focusBorder: personalization.input.focusBorder,
                                focusRing: personalization.input.focusRing,
                                text: personalization.input.text,
                                placeholder: personalization.input.placeholder,
                                borderRadius: personalization.input.borderRadius,
                              },
                              text: {
                                primary: personalization.text.primary,
                                secondary: personalization.text.secondary,
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {isEditing && (
                  <button
                    type="button"
                    onClick={() => { if (!isEditing) setIsEditing(true); appendContact({ name: '', position: '', email: '', phone: '' }); }}
                    className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <Plus size={16} />
                    {t('admin.configuration.contact.internal.add')}
                  </button>
                )}
              </div>
            )}

            {/* TAB: Socials */}
            {activeTab === 'socials' && (
              <div className="space-y-6">
                <p className="text-sm text-foreground-secondary italic">
                  {t('admin.configuration.contact.socials.description')}
                </p>

                <div className="space-y-4">
                  {socialFields.map((field, index) => {
                    // Find selected network to display icon
                    const selectedKey = form.watch(`socials.${index}.key`);
                    const selectedNetwork = SOCIAL_NETWORKS.find(n => n.value === selectedKey);
                    const Icon = selectedNetwork?.icon || Share2;

                    return (
                      <div key={field.id} className="flex gap-3 items-center">
                        <div className="flex-1 relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Icon className="h-4 w-4 text-foreground-secondary" />
                          </div>
                          <select
                            {...form.register(`socials.${index}.key` as const, {
                              validate: (val) => {
                                if (!isEditing) return true;
                                const url = form.getValues(`socials.${index}.value`);
                                if (url && !val) return t('admin.configuration.contact.socials.networkRequired');
                                return true;
                              }
                            })}
                            disabled={!isEditing}
                            className={`${inputClasses} pl-10 appearance-none bg-surface ${form.formState.errors.socials?.[index]?.key ? 'border-error' : ''}`}
                          >
                            <option value="">{t('admin.configuration.contact.socials.select')}</option>
                            {SOCIAL_NETWORKS.map((network) => (
                              <option key={network.value} value={network.value}>
                                {network.label}
                              </option>
                            ))}
                            <option value="other">{t('admin.configuration.contact.socials.other')}</option>
                          </select>
                          {form.formState.errors.socials?.[index]?.key && (
                            <p className="mt-1 text-xs text-error">
                              {form.formState.errors.socials[index]?.key?.message}
                            </p>
                          )}
                        </div>

                        {/* Show custom key input if "other" is selected */}
                        {selectedKey === 'other' && (
                          <div className="flex-1">
                            <input
                              placeholder={t('admin.configuration.contact.socials.namePlaceholder')}
                              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-background-tertiary text-sm text-foreground bg-surface"
                              // The onChange handler was empty and not using 'e', so it's removed.
                              // The input is disabled, so onChange is not needed anyway.
                              disabled={true} // Disabled for now as logic is complex
                              value="Autre (Lien générique)"
                            />
                          </div>
                        )}

                        <div className="flex-[2]">
                          <input
                            {...form.register(`socials.${index}.value` as const, {
                              validate: (val) => {
                                if (!isEditing) return true;
                                const key = form.getValues(`socials.${index}.key`);
                                if (key && !val) return t('admin.configuration.contact.socials.urlRequired');
                                return true;
                              }
                            })}
                            disabled={!isEditing}
                            placeholder={t('admin.configuration.contact.socials.urlPlaceholder')}
                            className={`${inputClasses} ${form.formState.errors.socials?.[index]?.value ? 'border-error' : ''}`}
                          />
                          {form.formState.errors.socials?.[index]?.value && (
                            <p className="mt-1 text-xs text-error">
                              {form.formState.errors.socials[index]?.value?.message}
                            </p>
                          )}
                        </div>
                        {isEditing && (
                          <button
                            type="button"
                            onClick={() => removeSocial(index)}
                            className="p-2 text-error hover:bg-error-10 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => { if (!isEditing) setIsEditing(true); appendSocial({ key: '', value: '' }); }}
                      className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-dark px-3 py-2 rounded-lg hover:bg-primary-10 transition-colors"
                    >
                      <Plus size={16} />
                      {t('admin.configuration.contact.socials.add')}
                    </button>
                  )}
                </div>
              </div>
            )}

          </form>
        </CardBody>
      </Card>
    </div>
  );
};

export default ContactInformationConfig;
